import { SortingState, VisibilityState } from '@tanstack/react-table';
import { produce } from 'immer';
import { isEmpty, isEqual, keys, mapValues, without } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export type EnumFilter = Record<string, boolean>;

export type DateFilter = {
  startDate?: Date;
  endDate?: Date;
};

export type FilterState = Record<string, EnumFilter | DateFilter | undefined>;

export type DateFilterKeys<TFilterState extends FilterState> = {
  [P in keyof TFilterState]: TFilterState[P] extends DateFilter ? P : never;
}[keyof TFilterState];

export type EnumFilterKeys<TFilterState extends FilterState> = {
  [P in keyof TFilterState]: TFilterState[P] extends EnumFilter | undefined ? P : never;
}[keyof TFilterState];

export type FilterValuesArray<TFilterState extends FilterState> = {
  [P in EnumFilterKeys<TFilterState>]?: TFilterState[P] extends EnumFilter ? (keyof TFilterState[P])[] : never;
};

export type TableState<TFilterState extends FilterState> = {
  sorting: {
    sortBy: SortingState;
    setSortBy: React.Dispatch<React.SetStateAction<SortingState>>;
    resetSortBy: () => void;
  };

  search: {
    searchQuery: string;
    setSearchQuery: (s: string) => void;
    resetSearchQuery: () => void;
  };

  pagination: {
    pageIndex: number;
    pageSize: number;
    setPageIndex: (n: number) => void;
    setPageSize: (n: number) => void;
  };

  filters: {
    filterState: TFilterState;
    setFilter: (filterState: FilterValuesArray<TFilterState>, resetOtherFilters?: boolean) => void;
    toggleFilterValue: <T extends EnumFilterKeys<TFilterState>>(key: T, value: keyof TFilterState[T]) => void;
    setDateFilter: <K extends DateFilterKeys<TFilterState>>(filterKey: K, value: DateFilter) => void;
    selectAllValues: <T extends keyof TFilterState>(key: T) => void;
    filteredColumns: string[];
    isFiltered: boolean;
    resetFilters: () => void;
  };

  selection: {
    selected: string[];
    allSelected: boolean;
    setSelectedId: (id: string, value: boolean) => void;
    setSelectedIds: (ids: string[]) => void;
    setAllSelected: (s: boolean) => void;
    resetSelected: () => void;
  };

  columns: {
    columnVisibility: VisibilityState;
    setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
  };

  resetTable: (opts?: {
    sortBy: boolean;
    search: boolean;
    pagination: boolean;
    selection: boolean;
    filters: boolean;
    columns: boolean;
  }) => void;
};

const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_PAGE_SIZE = 20;

export const useTableState = <TFilterState extends FilterState>({
  allFilters,
  defaultFilters,
  defaultPageIndex,
  defaultPageSize,
  defaultSearch,
  defaultColumnVisibility,
  defaultSortBy,
}: {
  allFilters: TFilterState;
  defaultFilters?: FilterValuesArray<TFilterState>;
  defaultPageIndex?: number;
  defaultPageSize?: number;
  defaultSearch?: string;
  defaultColumnVisibility?: VisibilityState;
  defaultSortBy?: SortingState;
}): TableState<TFilterState> => {
  // Selection
  const [selected, setSelected] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState(false);

  const resetSelected = useCallback(() => {
    setSelected([]);
    setAllSelected(false);
  }, []);

  const setSelectedId = useCallback(
    (id: string, value: boolean) => {
      if (!value) {
        setSelected(without(selected, id));
      } else {
        setSelected([...selected, id]);
      }
      setAllSelected(false);
    },
    [selected],
  );

  // Sorting
  const [sortBy, setSortBy] = useState<SortingState>(defaultSortBy || []);
  const resetSortBy = useCallback(() => setSortBy(defaultSortBy || []), [defaultSortBy]);

  // Pagination
  const [pageIndex, setPageIndex] = useState(defaultPageIndex ?? DEFAULT_PAGE_INDEX);
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE);
  const resetPagination = useCallback(() => {
    setPageIndex(DEFAULT_PAGE_INDEX);
    setPageSize(DEFAULT_PAGE_SIZE);
  }, []);

  // Narrowing the results has to send the user back to the first page, but their chosen page size is a
  // preference that should survive a filter change
  const resetPageIndex = useCallback(() => setPageIndex(DEFAULT_PAGE_INDEX), []);

  // Search
  const [searchQuery, setSearchQuery] = useState(defaultSearch ?? '');
  const setSearch = useCallback(
    (q: string) => {
      resetSelected();
      resetPagination();
      setSearchQuery(q);
    },
    [resetPagination, resetSelected],
  );
  const resetSearch = () => setSearchQuery('');

  // Filters
  const mapFilterArrayToFilterState = useCallback(
    (filterInput: FilterValuesArray<TFilterState>) =>
      mapValues(filterInput, (filterValues, filterKey) => {
        return mapValues(allFilters[filterKey], (_, key) => (filterValues || ([] as string[])).includes(key));
      }),
    [allFilters],
  );

  const initialFilters = useMemo(
    () => (defaultFilters ? { ...allFilters, ...mapFilterArrayToFilterState(defaultFilters) } : allFilters),
    [mapFilterArrayToFilterState, allFilters, defaultFilters],
  );

  const [filters, setFilters] = useState<TFilterState>(initialFilters);

  const setFilter = useCallback(
    (filterInput: FilterValuesArray<TFilterState>, resetOtherFilters?: boolean) => {
      const partialFilter = mapFilterArrayToFilterState(filterInput);
      const baseToMerge = resetOtherFilters ? initialFilters : filters;
      resetSelected();
      resetPageIndex();
      setFilters({ ...baseToMerge, ...partialFilter });
    },
    [initialFilters, mapFilterArrayToFilterState, filters, resetPageIndex, resetSelected],
  );

  const selectAllValues = useCallback(
    <T extends keyof TFilterState>(filterKey: T, selectAllByClearing?: boolean) => {
      const newFilters = produce<TFilterState>(filters, (draft: any) => {
        if (selectAllByClearing) {
          draft[filterKey] = Object.create(null);
        } else {
          for (const valueKey of Object.keys(draft[filterKey])) {
            draft[filterKey][valueKey] = true;
          }
        }
      });
      resetSelected();
      resetPageIndex();
      setFilters(newFilters);
    },
    [filters, resetPageIndex, resetSelected],
  );

  const toggleFilterValue = useCallback(
    <T extends EnumFilterKeys<TFilterState>>(
      filterKey: T,
      value: keyof TFilterState[T],
      clearOtherValues?: boolean,
    ) => {
      const filter = filters[filterKey];

      if (isEnumFilter(filter)) {
        const currentValue = filter[value as string];
        const newFilters = produce<TFilterState>(filters, (draft: any) => {
          if (clearOtherValues) {
            draft[filterKey] = mapValues(draft[filterKey], (_, key) => key === value);
          } else {
            draft[filterKey][value] = !currentValue;
          }
        });
        resetSelected();
        resetPageIndex();
        setFilters(newFilters);
      }
    },
    [filters, resetPageIndex, resetSelected],
  );

  const setDateFilter = useCallback(
    <K extends DateFilterKeys<TFilterState>>(filterKey: K, value: DateFilter) => {
      const newFilters = produce<TFilterState>(filters, (draft: any) => {
        draft[filterKey] = value;
      });
      resetSelected();
      resetPageIndex();
      setFilters(newFilters);
    },
    [filters, resetPageIndex, resetSelected],
  );

  const filteredColumns = useMemo(() => {
    const cols: string[] = [];
    for (const key of keys(initialFilters)) {
      if (!isEqual(initialFilters[key], filters[key])) {
        cols.push(key);
      }
    }
    return cols;
  }, [initialFilters, filters]);

  const isFiltered = useMemo(() => !isEqual(initialFilters, filters), [initialFilters, filters]);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultColumnVisibility || {});
  const resetColumnVisibility = useCallback(
    () => setColumnVisibility(defaultColumnVisibility || {}),
    [defaultColumnVisibility],
  );

  const resetTable = useCallback(
    (
      opts: {
        sortBy: boolean;
        search: boolean;
        pagination: boolean;
        selection: boolean;
        filters: boolean;
        columns: boolean;
      } = {
        sortBy: true,
        search: true,
        pagination: true,
        selection: true,
        filters: true,
        columns: true,
      },
    ) => {
      if (opts.sortBy) resetSortBy();
      if (opts.search) resetSearch();
      if (opts.pagination) resetPagination();
      if (opts.selection) resetSelected();
      if (opts.filters) resetFilters();
      if (opts.columns) resetColumnVisibility();
    },
    [resetPagination, resetSelected, resetFilters, resetColumnVisibility, resetSortBy],
  );

  return {
    sorting: {
      sortBy,
      setSortBy,
      resetSortBy,
    },

    search: {
      searchQuery,
      setSearchQuery: setSearch,
      resetSearchQuery: resetSearch,
    },

    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },

    filters: {
      filterState: filters,
      setFilter,
      toggleFilterValue,
      setDateFilter,
      filteredColumns,
      isFiltered,
      resetFilters,
      selectAllValues,
    },

    selection: {
      selected,
      allSelected,
      setSelectedId,
      setSelectedIds: setSelected,
      setAllSelected,
      resetSelected,
    },

    columns: {
      columnVisibility,
      setColumnVisibility,
    },

    resetTable,
  };
};

export const parseSortBy = (sort: SortingState) => {
  if (!sort || isEmpty(sort)) {
    return undefined;
  }
  return { key: sort[0].id, order: sort[0].desc ? 'desc' : 'asc' };
};

export const isEnumFilter = (filter: EnumFilter | DateFilter | undefined): filter is EnumFilter => {
  return (!filter?.startDate && !filter?.endDate) || filter == null;
};

export const isDateFilter = (filter: EnumFilter | DateFilter | undefined): filter is DateFilter => {
  return (!!filter && ('startDate' in filter || 'endDate' in filter)) || isEmpty(filter);
};
